import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateConnector, useSetOAuthClientSecret, type CreateConnectorInput } from "@/api/mutations/connectors";
import type { ConnectorAuth } from "@/types";
import { buildOAuthAuth as buildOAuthAuthPure, buildClientCredentialsAuth } from "./connectorAuthForm";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

// unified-managed-mcp: eine deklarierte ENV-Variable eines lokalen Connectors (UI-Zeile).
interface EnvRow {
  name: string;
  secret: boolean;
  required: boolean;
}

const EMPTY = {
  name: "",
  description: "",
  url: "",
  transport: "auto",
  // #253 (UC-A) / #336: Auth-kind (none|static|oauth|login|client-credentials) — transport-abhängig erlaubt.
  authKind: "none",
  // static (API-Key/Bearer)
  header: "Authorization",
  scheme: "Bearer",
  // oauth (MCP-Discovery/DCR)
  clientId: "",
  scopes: "",
  authServerHint: "",
  clientSecret: "",
  // #336 (UC-2): explizite OAuth-Endpoints (Discovery-Bypass) — beide oder keiner.
  authorizationEndpoint: "",
  tokenEndpoint: "",
  // #336 (UC-1): client-credentials (Service-Identität)
  ccTokenUrl: "",
  ccScopes: "",
  ccAuthStyle: "basic",
  // #253 (UC-A): login (user/pw → JWT)
  loginTokenUrl: "",
  loginTokenPath: "access_token",
  loginBodyFormat: "json",
  loginUserField: "username",
  loginPassField: "password",
  loginInjectHeader: "Authorization",
  loginInjectScheme: "Bearer",
  loginTtl: "",
  // local (stdio)
  command: "",
  args: "",
  // #249: managed CLI-Tool (transport=cli)
  bin: "",
  installCmd: "",
  cliAuthShape: "env",
  binding: "",
  // #253: generischer REST-API-Connector (transport=openapi)
  baseUrl: "",
  specUrl: "",
  openapiMode: "dynamic",
  includeTags: "",
};

export function CreateConnectorDialog({ open, onOpenChange, onCreated }: Props) {
  const [form, setForm] = useState({ ...EMPTY });
  const [envRows, setEnvRows] = useState<EnvRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const mutation = useCreateConnector();
  const setClientSecret = useSetOAuthClientSecret();
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const isLocal = form.transport === "local";
  const isCli = form.transport === "cli";
  const isOpenApi = form.transport === "openapi";
  const isRemote = !isLocal && !isCli && !isOpenApi;
  const cliNeedsBinding = isCli && (form.cliAuthShape === "env" || form.cliAuthShape === "file" || form.cliAuthShape === "oauth");
  const cliOAuth = isCli && form.cliAuthShape === "oauth";

  function close(o: boolean) {
    if (!o) {
      setForm({ ...EMPTY });
      setEnvRows([]);
      setError(null);
    }
    onOpenChange(o);
  }

  // #336 (UC-2): die oauth-Auth — EINE Quelle für remote (authKind oauth) UND cli mit
  // oauth-Shape (vorher hatte der cli-Pfad eine Inline-Kopie, die die expliziten Endpoints
  // stillschweigend verwarf — Review-Fund). Der Zusammenbau selbst ist die reine, getestete
  // Funktion in connectorAuthForm.ts; hier nur noch das Anzeigen des Fehlers.
  function buildOAuthAuth(): ConnectorAuth | null {
    const r = buildOAuthAuthPure({
      clientId: form.clientId,
      scopes: form.scopes,
      authServerHint: form.authServerHint,
      authorizationEndpoint: form.authorizationEndpoint,
      tokenEndpoint: form.tokenEndpoint,
    });
    if (!r.ok) {
      setError(r.error);
      return null;
    }
    return r.auth;
  }

  // #253 (UC-A): die ConnectorAuth-Union aus den Formularfeldern bauen (für remote + openapi).
  // Wirft via setError (gibt null zurück) bei Pflichtfeld-Verletzung.
  function buildAuth(): ConnectorAuth | null {
    if (form.authKind === "static") {
      if (!form.header.trim()) {
        setError("API-Key: Header-Name ist erforderlich");
        return null;
      }
      return { kind: "static", header: form.header.trim(), scheme: form.scheme.trim() || "Bearer" };
    }
    if (form.authKind === "oauth") return buildOAuthAuth();
    // #336 (UC-1): client-credentials — nur nicht-geheime Config; clientId/clientSecret
    // folgen beim Verbinden (write-only → Vault).
    if (form.authKind === "client-credentials") {
      const r = buildClientCredentialsAuth({ tokenUrl: form.ccTokenUrl, scopes: form.ccScopes, authStyle: form.ccAuthStyle });
      if (!r.ok) {
        setError(r.error);
        return null;
      }
      return r.auth;
    }
    if (form.authKind === "login") {
      if (!form.loginTokenUrl.trim()) {
        setError("Login: Token-URL ist erforderlich");
        return null;
      }
      if (!form.loginTokenPath.trim()) {
        setError("Login: Token-Pfad ist erforderlich");
        return null;
      }
      const ttl = parseInt(form.loginTtl.trim(), 10);
      return {
        kind: "login",
        tokenUrl: form.loginTokenUrl.trim(),
        bodyFormat: form.loginBodyFormat === "form" ? "form" : "json",
        userField: form.loginUserField.trim() || "username",
        passField: form.loginPassField.trim() || "password",
        tokenPath: form.loginTokenPath.trim(),
        injectHeader: form.loginInjectHeader.trim() || "Authorization",
        injectScheme: form.loginInjectScheme.trim() || "Bearer",
        ttlSeconds: Number.isFinite(ttl) && ttl > 0 ? ttl : null,
      };
    }
    return { kind: "none" };
  }

  function submit() {
    setError(null);
    if (!form.name.trim()) return setError("Name ist erforderlich");

    let input: CreateConnectorInput;
    // App-Secret eines vertraulichen OAuth-Clients (nach Create separat → Vault).
    let clientSecret = "";

    if (isLocal) {
      if (!form.command.trim()) return setError("Lokal: command ist erforderlich");
      for (const r of envRows) {
        if (r.name.trim() && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(r.name.trim())) {
          return setError(`Ungültiger ENV-Name: "${r.name}"`);
        }
      }
      input = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        transport: "local",
        auth: { kind: "none" },
        local: {
          command: form.command.trim(),
          args: form.args.split(/\s+/).map((a) => a.trim()).filter(Boolean),
          envSchema: envRows
            .filter((r) => r.name.trim())
            .map((r) => ({ name: r.name.trim(), secret: r.secret, required: r.required })),
        },
      };
    } else if (isCli) {
      if (!form.bin.trim()) return setError("CLI: bin ist erforderlich");
      if (cliNeedsBinding && !form.binding.trim()) return setError(`CLI authShape '${form.cliAuthShape}' braucht ein binding`);
      for (const r of envRows) {
        if (r.name.trim() && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(r.name.trim())) return setError(`Ungültiger ENV-Name: "${r.name}"`);
      }
      // cli mit oauth-Shape nutzt die #231-OAuth-Auth (inkl. UC-2-Endpoints, gleiche
      // Paar-Prüfung wie remote); sonst kein Header-Auth (none).
      let auth: ConnectorAuth = { kind: "none" };
      if (cliOAuth) {
        const a = buildOAuthAuth();
        if (!a) return;
        auth = a;
      }
      input = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        transport: "cli",
        auth,
        cli: {
          bin: form.bin.trim(),
          installCmd: form.installCmd.trim() || null,
          authShape: form.cliAuthShape,
          binding: cliNeedsBinding ? form.binding.trim() : null,
          extraEnv: envRows
            .filter((r) => r.name.trim())
            .map((r) => ({ name: r.name.trim(), secret: r.secret, required: r.required })),
        },
      };
      if (cliOAuth) clientSecret = form.clientSecret.trim();
    } else if (isOpenApi) {
      if (!form.baseUrl.trim()) return setError("OpenAPI: baseUrl ist erforderlich");
      if (!form.specUrl.trim()) return setError("OpenAPI: Spec-URL ist erforderlich");
      const auth = buildAuth();
      if (!auth) return; // buildAuth hat setError gesetzt
      input = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        transport: "openapi",
        auth,
        openapi: {
          baseUrl: form.baseUrl.trim(),
          specUrl: form.specUrl.trim(),
          mode: form.openapiMode,
          includeTags: form.includeTags.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean),
        },
      };
    } else {
      if (!form.url.trim()) return setError("URL ist erforderlich");
      const auth = buildAuth();
      if (!auth) return;
      input = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        url: form.url.trim(),
        transport: form.transport,
        auth,
      };
      if (auth.kind === "oauth") clientSecret = form.clientSecret.trim();
    }

    mutation.mutate(input, {
      onSuccess: (c) => {
        // unified-managed-mcp: weiche Warnungen (z.B. Version nicht gepinnt) sichtbar machen.
        if (c.warnings?.length) c.warnings.forEach((w) => toast.warning(w));
        if (clientSecret) {
          setClientSecret.mutate(
            { id: c.id, value: clientSecret },
            {
              onSuccess: () => toast.success(`Connector "${c.name}" angelegt + Client-Secret gespeichert`),
              onError: (err) => toast.error(`Connector angelegt, aber Client-Secret fehlgeschlagen: ${(err as Error).message}`),
            },
          );
        } else {
          toast.success(`Connector "${c.name}" angelegt`);
        }
        onCreated?.(c.id);
        close(false);
      },
      onError: (err) => {
        const msg = (err as Error).message ?? "Fehler";
        setError(msg);
        toast.error(`Anlegen fehlgeschlagen: ${msg}`);
      },
    });
  }

  // Auth-kind-Optionen je Transport (Domain erzwingt dasselbe). local/cli haben keinen Select.
  // #336 (UC-1): client-credentials ist auf BEIDEN Achsen erlaubt (remote + openapi).
  const authKindOptions = isOpenApi
    ? ["none", "static", "login", "client-credentials"]
    : ["none", "static", "oauth", "client-credentials"];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="create-connector-dialog">
        <DialogHeader>
          <DialogTitle>Neuer Connector</DialogTitle>
          <DialogDescription>
            Remote-MCP (URL + Auth), lokaler stdio-MCP, managed CLI-Tool oder REST-API (OpenAPI-Spec → generierte MCP-Tools). Secrets folgen beim Verbinden (Vault).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="conn-name">Name *</Label>
            <Input id="conn-name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Notion" data-testid="conn-field-name" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="conn-desc">Beschreibung</Label>
            <Input id="conn-desc" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="MCP …" />
          </div>

          <div className="flex gap-3">
            <div className="flex-1 space-y-1">
              <Label>Transport</Label>
              <Select
                value={form.transport}
                onValueChange={(v) => set({ transport: v, authKind: "none" })}
              >
                <SelectTrigger data-testid="conn-field-transport"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">auto (remote)</SelectItem>
                  <SelectItem value="streamable-http">streamable-http (remote)</SelectItem>
                  <SelectItem value="sse">sse (remote)</SelectItem>
                  <SelectItem value="local">local (stdio)</SelectItem>
                  <SelectItem value="cli">cli (managed CLI-Tool)</SelectItem>
                  <SelectItem value="openapi">openapi (REST-API)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(isRemote || isOpenApi) && (
              <div className="flex-1 space-y-1">
                <Label>Auth</Label>
                <Select value={form.authKind} onValueChange={(v) => set({ authKind: v })}>
                  <SelectTrigger data-testid="conn-field-authkind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {authKindOptions.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k === "none"
                          ? "none"
                          : k === "static"
                            ? "static (API-Key/Bearer)"
                            : k === "oauth"
                              ? "oauth (MCP)"
                              : k === "client-credentials"
                                ? "client-credentials (Service-Identität)"
                                : "login (user/pw → JWT)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {isRemote && (
            <div className="space-y-1">
              <Label htmlFor="conn-url">MCP-URL *</Label>
              <Input id="conn-url" value={form.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://mcp.example.com/mcp" className="mono text-[12px]" data-testid="conn-field-url" />
            </div>
          )}

          {isOpenApi && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="conn-baseurl">API-Basis-URL *</Label>
                <Input id="conn-baseurl" value={form.baseUrl} onChange={(e) => set({ baseUrl: e.target.value })} placeholder="https://api.example.com/v2" className="mono text-[12px]" data-testid="conn-field-baseurl" />
                <p className="text-[11px] text-text3">Basis-Adresse, gegen die die generierten Tool-Calls laufen.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="conn-specurl">OpenAPI-/Swagger-Spec-URL *</Label>
                <Input id="conn-specurl" value={form.specUrl} onChange={(e) => set({ specUrl: e.target.value })} placeholder="https://api.example.com/v2/swagger.json" className="mono text-[12px]" data-testid="conn-field-specurl" />
                <p className="text-[11px] text-text3">Aus dieser Spec werden die MCP-Tools generiert (npx @ivotoby/openapi-mcp-server im Brain).</p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label>Tool-Modus</Label>
                  <Select value={form.openapiMode} onValueChange={(v) => set({ openapiMode: v })}>
                    <SelectTrigger data-testid="conn-field-openapimode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dynamic">dynamic (3 Meta-Tools — empfohlen)</SelectItem>
                      <SelectItem value="all">all (ein Tool je Operation)</SelectItem>
                      <SelectItem value="explicit">explicit (nur via Tags)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-text3">dynamic vermeidet die Tool-Flut großer APIs.</p>
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="conn-tags">Tags <span className="text-text3">(optional)</span></Label>
                  <Input id="conn-tags" value={form.includeTags} onChange={(e) => set({ includeTags: e.target.value })} placeholder="pet store" className="mono text-[12px]" data-testid="conn-field-includetags" />
                  <p className="text-[11px] text-text3">Leerzeichen-getrennt — beschränkt auf Operationen mit diesen Tags.</p>
                </div>
              </div>
            </div>
          )}

          {isLocal && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="conn-command">Command *</Label>
                <Input id="conn-command" value={form.command} onChange={(e) => set({ command: e.target.value })} placeholder="npx · uvx · /usr/local/bin/mcp-server" className="mono text-[12px]" data-testid="conn-field-command" />
                <p className="text-[11px] text-text3">npx/uvx benötigen das Platform-Setting „on-the-fly-Fetch"; sonst nur ins Image gebackene Binaries.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="conn-args">Args <span className="text-text3">(Leerzeichen-getrennt)</span></Label>
                <Input id="conn-args" value={form.args} onChange={(e) => set({ args: e.target.value })} placeholder="-y @modelcontextprotocol/server-github@1.2.3" className="mono text-[12px]" data-testid="conn-field-args" />
                <p className="text-[11px] text-text3">Empfehlung: Version pinnen (name@1.2.3 bzw. name==1.2.3) — sonst Warnung.</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label>ENV-Variablen <span className="text-text3">(Werte beim Verbinden)</span></Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setEnvRows((r) => [...r, { name: "", secret: true, required: true }])} data-testid="conn-env-add">+ Variable</Button>
                </div>
                {envRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={row.name}
                      onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                      placeholder="GITHUB_TOKEN"
                      className="mono text-[12px] flex-1"
                    />
                    <label className="flex items-center gap-1 text-[11px] text-text2">
                      <input type="checkbox" checked={row.secret} onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, secret: e.target.checked } : r)))} /> secret
                    </label>
                    <label className="flex items-center gap-1 text-[11px] text-text2">
                      <input type="checkbox" checked={row.required} onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, required: e.target.checked } : r)))} /> required
                    </label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEnvRows((rows) => rows.filter((_, j) => j !== i))}>✕</Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isCli && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="conn-bin">bin *</Label>
                <Input id="conn-bin" value={form.bin} onChange={(e) => set({ bin: e.target.value })} placeholder="gws · gh · aws" className="mono text-[12px]" data-testid="conn-field-bin" />
                <p className="text-[11px] text-text3">Executable. Der run_cli-Broker führt es außerhalb der Agent-Sandbox aus + injiziert den Cred (der Agent sieht ihn nie).</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="conn-installcmd">Install-Befehl <span className="text-text3">(optional)</span></Label>
                <Input id="conn-installcmd" value={form.installCmd} onChange={(e) => set({ installCmd: e.target.value })} placeholder="npm i -g @google/gws-cli" className="mono text-[12px]" />
                <p className="text-[11px] text-text3">Leer = der Agent installiert selbst. Sonst beim Brain-Boot ausgeführt.</p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label>Auth-Shape</Label>
                  <Select value={form.cliAuthShape} onValueChange={(v) => set({ cliAuthShape: v })}>
                    <SelectTrigger data-testid="conn-field-cliauthshape"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">none (kein Cred)</SelectItem>
                      <SelectItem value="env">env (Wert → ENV-Var)</SelectItem>
                      <SelectItem value="file">file (Wert → Datei)</SelectItem>
                      <SelectItem value="oauth">oauth (gebrokerter Token → ENV)</SelectItem>
                      <SelectItem value="self-managed">self-managed (CLI verwaltet selbst)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {cliNeedsBinding && (
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="conn-binding">{form.cliAuthShape === "file" ? "Datei-Pfad *" : "ENV-Variable *"}</Label>
                    <Input id="conn-binding" value={form.binding} onChange={(e) => set({ binding: e.target.value })} placeholder={form.cliAuthShape === "file" ? "/home/broker/.config/sa.json" : "GOOGLE_WORKSPACE_CLI_TOKEN"} className="mono text-[12px]" data-testid="conn-field-binding" />
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label>Zusätzliche ENV <span className="text-text3">(nicht-geheime Config)</span></Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setEnvRows((r) => [...r, { name: "", secret: false, required: false }])}>+ Variable</Button>
                </div>
                {envRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={row.name} onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))} placeholder="REGION" className="mono text-[12px] flex-1" />
                    <label className="flex items-center gap-1 text-[11px] text-text2"><input type="checkbox" checked={row.secret} onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, secret: e.target.checked } : r)))} /> secret</label>
                    <label className="flex items-center gap-1 text-[11px] text-text2"><input type="checkbox" checked={row.required} onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, required: e.target.checked } : r)))} /> required</label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEnvRows((rows) => rows.filter((_, j) => j !== i))}>✕</Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* static (API-Key/Bearer): Header-Name + Schema */}
          {(isRemote || isOpenApi) && form.authKind === "static" && (
            <div className="flex gap-3 rounded-md border border-border bg-surface2 p-3">
              <div className="flex-1 space-y-1">
                <Label htmlFor="conn-header">Header *</Label>
                <Input id="conn-header" value={form.header} onChange={(e) => set({ header: e.target.value })} placeholder="Authorization" className="mono text-[12px]" data-testid="conn-field-header" />
              </div>
              <div className="w-40 space-y-1">
                <Label htmlFor="conn-scheme">Schema</Label>
                <Input id="conn-scheme" value={form.scheme} onChange={(e) => set({ scheme: e.target.value })} placeholder="Bearer" />
              </div>
            </div>
          )}

          {/* oauth (remote MCP-Discovery/DCR) ODER cli mit oauth-Shape */}
          {((isRemote && form.authKind === "oauth") || cliOAuth) && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="conn-clientid">Client-ID <span className="text-text3">(leer = DCR)</span></Label>
                <Input id="conn-clientid" value={form.clientId} onChange={(e) => set({ clientId: e.target.value })} placeholder="leer lassen für Dynamic Client Registration" className="mono text-[12px]" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="conn-scopes">Scopes <span className="text-text3">(Leerzeichen-getrennt)</span></Label>
                <Input id="conn-scopes" value={form.scopes} onChange={(e) => set({ scopes: e.target.value })} placeholder="read write offline_access" className="mono text-[12px]" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="conn-ash">Auth-Server-Hint <span className="text-text3">(optional)</span></Label>
                <Input id="conn-ash" value={form.authServerHint} onChange={(e) => set({ authServerHint: e.target.value })} placeholder="https://login.microsoftonline.com/<tenant>/v2.0" className="mono text-[12px]" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="conn-clientsecret">Client-Secret <span className="text-text3">(optional — vertraulicher Client)</span></Label>
                <Input id="conn-clientsecret" type="password" value={form.clientSecret} onChange={(e) => set({ clientSecret: e.target.value })} placeholder="nur wenn der Provider ein App-Secret verlangt" className="mono text-[12px]" data-testid="conn-field-clientsecret" />
              </div>
              {/* #336 (UC-2): explizite Endpoints — für Anbieter, die keine RFC-8414-Metadata am
                  Host-Root liefern (z.B. Abacus: OIDC-Discovery unter einem Pfad). Beide oder keiner. */}
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="conn-authep">Authorize-Endpoint <span className="text-text3">(optional)</span></Label>
                  <Input id="conn-authep" value={form.authorizationEndpoint} onChange={(e) => set({ authorizationEndpoint: e.target.value })} placeholder="https://host/oauth/oauth2/v1/auth" className="mono text-[12px]" data-testid="conn-field-authep" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="conn-tokenep">Token-Endpoint <span className="text-text3">(optional)</span></Label>
                  <Input id="conn-tokenep" value={form.tokenEndpoint} onChange={(e) => set({ tokenEndpoint: e.target.value })} placeholder="https://host/oauth/oauth2/v1/token" className="mono text-[12px]" data-testid="conn-field-tokenep" />
                </div>
              </div>
              <p className="text-[11px] text-text3">
                Beide gesetzt = keine Discovery und kein DCR (Client-ID wird dann Pflicht). Beide leer = Discovery wie bisher.
              </p>
            </div>
          )}

          {/* #336 (UC-1) client-credentials (Service-Identität) — remote + openapi */}
          {(isRemote || isOpenApi) && form.authKind === "client-credentials" && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3" data-testid="conn-cc-block">
              <div className="space-y-1">
                <Label htmlFor="conn-cc-url">Token-URL *</Label>
                <Input id="conn-cc-url" value={form.ccTokenUrl} onChange={(e) => set({ ccTokenUrl: e.target.value })} placeholder="https://host/oauth/oauth2/v1/token" className="mono text-[12px]" data-testid="conn-field-cc-url" />
                <p className="text-[11px] text-text3">Der Master holt den Token selbst (grant_type=client_credentials) und cacht ihn bis zum Ablauf — das LLM sieht ihn nie.</p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="conn-cc-scopes">Scopes <span className="text-text3">(Leerzeichen-getrennt)</span></Label>
                  <Input id="conn-cc-scopes" value={form.ccScopes} onChange={(e) => set({ ccScopes: e.target.value })} placeholder="API" className="mono text-[12px]" data-testid="conn-field-cc-scopes" />
                </div>
                <div className="w-56 space-y-1">
                  <Label>Client-Auth</Label>
                  <Select value={form.ccAuthStyle} onValueChange={(v) => set({ ccAuthStyle: v })}>
                    <SelectTrigger data-testid="conn-field-cc-authstyle"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">basic (Authorization-Header)</SelectItem>
                      <SelectItem value="body">body (client_id/secret im Body)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-text3">Client-ID + Client-Secret folgen beim Verbinden (write-only → Vault).</p>
            </div>
          )}

          {/* #253 (UC-A) login (user/pw → JWT) — nur openapi */}
          {isOpenApi && form.authKind === "login" && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="conn-login-url">Login-Token-URL *</Label>
                <Input id="conn-login-url" value={form.loginTokenUrl} onChange={(e) => set({ loginTokenUrl: e.target.value })} placeholder="https://api.example.com/auth/login" className="mono text-[12px]" data-testid="conn-field-login-url" />
                <p className="text-[11px] text-text3">Endpoint, an den der Master user/pw postet. Token + Refresh holt der Master selbst (LLM sieht ihn nie).</p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="conn-login-path">Token-Pfad *</Label>
                  <Input id="conn-login-path" value={form.loginTokenPath} onChange={(e) => set({ loginTokenPath: e.target.value })} placeholder="access_token · data.token" className="mono text-[12px]" data-testid="conn-field-login-path" />
                  <p className="text-[11px] text-text3">Dot-Pfad zum Token in der Antwort.</p>
                </div>
                <div className="w-40 space-y-1">
                  <Label>Body-Format</Label>
                  <Select value={form.loginBodyFormat} onValueChange={(v) => set({ loginBodyFormat: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="form">Form</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="conn-login-userfield">User-Feld</Label>
                  <Input id="conn-login-userfield" value={form.loginUserField} onChange={(e) => set({ loginUserField: e.target.value })} placeholder="username" className="mono text-[12px]" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="conn-login-passfield">Passwort-Feld</Label>
                  <Input id="conn-login-passfield" value={form.loginPassField} onChange={(e) => set({ loginPassField: e.target.value })} placeholder="password" className="mono text-[12px]" />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="conn-login-injheader">Inject-Header</Label>
                  <Input id="conn-login-injheader" value={form.loginInjectHeader} onChange={(e) => set({ loginInjectHeader: e.target.value })} placeholder="Authorization" className="mono text-[12px]" />
                </div>
                <div className="w-32 space-y-1">
                  <Label htmlFor="conn-login-injscheme">Scheme</Label>
                  <Input id="conn-login-injscheme" value={form.loginInjectScheme} onChange={(e) => set({ loginInjectScheme: e.target.value })} placeholder="Bearer" className="mono text-[12px]" />
                </div>
                <div className="w-32 space-y-1">
                  <Label htmlFor="conn-login-ttl">TTL (s)</Label>
                  <Input id="conn-login-ttl" value={form.loginTtl} onChange={(e) => set({ loginTtl: e.target.value })} placeholder="auto" className="mono text-[12px]" />
                </div>
              </div>
              <p className="text-[11px] text-text3">TTL leer = aus JWT-Ablauf bzw. expires_in ableiten; sonst Re-Login nach N Sekunden.</p>
            </div>
          )}

          {error && <div className="text-xs text-danger" data-testid="conn-error">{error}</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={mutation.isPending} data-testid="conn-submit">
            {mutation.isPending ? "…" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
